import React, { useEffect, useMemo, useState } from 'react';
import { MDBCol, MDBRow } from 'mdb-react-ui-kit';
import { FormGroup, Input, Label } from 'reactstrap';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import Swal from 'sweetalert2';
import SwitchButton3 from '../../../componentes/SwitchButton3';
import axios from '../../../utils/axiosConfig';

const ALERT_TYPES = [
    { label: 'APERTURA', base: 'Apertura' },
    { label: 'BATERIA BAJA', base: 'Bateria' },
    { label: 'ZONA SEGURA', base: 'ZonaSegura' },
    { label: 'GEOCERCAS', base: 'Geocercas' },
    { label: 'DESVIO', base: 'Desvio' },
    { label: 'RETROCESO', base: 'Retroceso' },
    { label: 'LUZ', base: 'Luz' },
    { label: 'GPS INVALIDO', base: 'GpsEstatico' },
    { label: 'TIEMPO REPORTE', base: 'TiempoGps' },
    { label: 'VELOCIDAD', base: 'Velocidad' },
];

const createEmptyAlertas = () => Object.fromEntries(
    ALERT_TYPES.map((type) => [type.base, { M: false, T: false }])
);

const createInitialForm = () => ({
    nombreContacto: '',
    empresaContacto: '',
    rolTrafico: '',
    movil: '',
    areaCode: '',
    email: '',
    alertas: createEmptyAlertas(),
    repTrafico: 'Desactivado',
    autoReporte: 'Desactivado',
});

const catalogCache = {
    empresas: new Map(),
    roles: new Map(),
};

const catalogRequests = {
    empresas: new Map(),
    roles: new Map(),
};

const getProjectKey = () => String(localStorage.getItem('project') || '');

const getRequestConfig = (signal) => ({
    signal,
    headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('tkn')}`,
    },
    withCredentials: true,
});

const fetchCachedCatalog = async ({ cacheType, cacheKey, endpoint, signal }) => {
    const cached = catalogCache[cacheType].get(cacheKey);
    if (cached) return cached;

    const pendingRequest = catalogRequests[cacheType].get(cacheKey);
    if (pendingRequest) return pendingRequest;

    const request = axios.post(
        `${process.env.REACT_APP_SERVER}${endpoint}`,
        JSON.stringify({ proyecto: cacheKey }),
        getRequestConfig(signal)
    ).then((response) => {
        const rows = Array.isArray(response?.data?.data) ? response.data.data : [];
        catalogCache[cacheType].set(cacheKey, rows);
        catalogRequests[cacheType].delete(cacheKey);
        return rows;
    }).catch((error) => {
        catalogRequests[cacheType].delete(cacheKey);
        throw error;
    });

    catalogRequests[cacheType].set(cacheKey, request);
    return request;
};

const FormModalCreateAlerta = ({ onClose, selectedAlerta }) => {
    const [tipoMail, setTipoMail] = useState('usuarios');
    const [empresas, setEmpresas] = useState([]);
    const [roles, setRoles] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [catalogLoading, setCatalogLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [form, setForm] = useState(createInitialForm);
    const projectKey = useMemo(() => getProjectKey(), []);

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;

        if (name.startsWith('alertas.')) {
            const [, alertType, channel] = name.split('.');
            setForm((current) => ({
                ...current,
                alertas: {
                    ...current.alertas,
                    [alertType]: {
                        ...current.alertas[alertType],
                        [channel]: checked,
                    },
                },
            }));
            return;
        }

        setForm((current) => ({
            ...current,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handlePhoneChange = (value, countryData) => {
        const dialCode = countryData?.dialCode || '';
        // Remove the dial code prefix to get the local number only
        const localNumber = dialCode && value.startsWith(dialCode)
            ? value.slice(dialCode.length)
            : value;
        setForm((current) => ({
            ...current,
            movil: localNumber,
            areaCode: dialCode,
        }));
    };

    const handleReset = () => {
        setTipoMail('usuarios');
        setSelectedId(null);
        setForm(createInitialForm());
        onClose();
    };

    const buildPayload = () => {
        const channels = Object.entries(form.alertas).flatMap(([base, channelState]) => ([
            [`${base}M`, Boolean(channelState.M)],
            [`${base}T`, Boolean(channelState.T)],
        ]));

        return {
            tipo: tipoMail === 'usuarios' ? 0 : 1,
            NombreContacto: form.nombreContacto,
            FKICEmpresa: parseInt(form.empresaContacto, 10),
            RolTrafico: form.rolTrafico || 0,
            Telefono: form.movil,
            AreaCode: form.areaCode,
            Mail: form.email,
            ReportTrafico: form.repTrafico === 'Activado',
            Autoreporte: form.autoReporte === 'Activado',
            ...Object.fromEntries(channels),
        };
    };

    const handleSubmit = async () => {
        if (submitLoading) return;

        const payload = buildPayload();
        const endpoint = selectedId
            ? '/operaciones2/setupdatecontacto'
            : '/operaciones2/setinsertcontacto';
        const requestPayload = selectedId
            ? {
                ...payload,
                RolTrafico: parseInt(form.rolTrafico, 10) || 0,
                ID: selectedId,
            }
            : payload;

        const operation = selectedId ? 'UPDATE' : 'CREATE';
        const endpoint_label = selectedId
            ? '/operaciones2/setupdatecontacto'
            : '/operaciones2/setinsertcontacto';

        console.group(`%c[Alertas][${operation}] → ${endpoint_label}`, 'color: #006699; font-weight: bold;');
        console.log('📋 Payload completo:', requestPayload);
        console.log('─── Información General ───');
        console.log('  tipo          :', requestPayload.tipo, `(${requestPayload.tipo === 0 ? 'usuarios' : 'proyectos'})`);
        console.log('  NombreContacto:', requestPayload.NombreContacto);
        console.log('  FKICEmpresa   :', requestPayload.FKICEmpresa);
        console.log('  RolTrafico    :', requestPayload.RolTrafico);
        console.log('  Telefono      :', requestPayload.Telefono, '← número sin código');
        console.log('  AreaCode      :', requestPayload.AreaCode, '← código de país sin +');
        console.log('  Mail          :', requestPayload.Mail);
        if (selectedId) console.log('  ID            :', requestPayload.ID);
        console.log('─── Flags ───');
        console.log('  ReportTrafico :', requestPayload.ReportTrafico);
        console.log('  Autoreporte   :', requestPayload.Autoreporte);
        console.log('─── Alertas (SMS=T / Mail=M) ───');
        ALERT_TYPES.forEach(({ label, base }) => {
            const sms = requestPayload[`${base}T`];
            const mail = requestPayload[`${base}M`];
            if (sms || mail) console.log(`  ${label.padEnd(16)}: SMS=${sms}, Mail=${mail}`);
        });
        console.groupEnd();

        setSubmitLoading(true);

        try {
            await axios.post(`${process.env.REACT_APP_SERVER}${endpoint}`, requestPayload);
            handleReset();
            Swal.fire({
                title: 'Éxito',
                text: selectedId ? 'Alerta actualizada correctamente' : 'Alerta creada correctamente',
                icon: 'success',
                confirmButtonText: 'Aceptar',
            });
        } catch (error) {
            Swal.fire({
                title: 'Error',
                text: selectedId ? 'Error al actualizar alerta' : 'Error al crear alerta',
                icon: 'error',
                confirmButtonText: 'Aceptar',
            });
            console.error('Error guardando alerta:', error);
        } finally {
            setSubmitLoading(false);
        }
    };

    useEffect(() => {
        let active = true;
        const controller = new AbortController();

        const loadCatalog = async () => {
            setCatalogLoading(true);

            try {
                if (tipoMail === 'usuarios') {
                    const rows = await fetchCachedCatalog({
                        cacheType: 'empresas',
                        cacheKey: projectKey,
                        endpoint: '/usuarios/getlistaempresa',
                        signal: controller.signal,
                    });
                    if (!active) return;
                    setEmpresas(rows);
                    setRoles([]);
                    return;
                }

                const rows = await fetchCachedCatalog({
                    cacheType: 'roles',
                    cacheKey: projectKey,
                    endpoint: '/usuarios/getlistaRolTrafico',
                    signal: controller.signal,
                });
                if (!active) return;
                setRoles(rows);
                setEmpresas([]);
            } catch (error) {
                if (error?.name === 'CanceledError' || error?.name === 'AbortError') return;
                console.error(`Error cargando ${tipoMail === 'usuarios' ? 'empresas' : 'roles'}:`, error);
            } finally {
                if (active) {
                    setCatalogLoading(false);
                }
            }
        };

        loadCatalog();

        return () => {
            active = false;
            controller.abort();
        };
    }, [tipoMail, projectKey]);

    useEffect(() => {
        if (selectedAlerta && Object.keys(selectedAlerta).length > 0) {
            if (typeof selectedAlerta.TipoContacto === 'number') {
                setTipoMail(selectedAlerta.TipoContacto === 0 ? 'usuarios' : 'proyectos');
            }

            const alertasFromApi = Object.fromEntries(
                ALERT_TYPES.map(({ base }) => [
                    base,
                    {
                        M: selectedAlerta[`${base}M`] === true,
                        T: selectedAlerta[`${base}T`] === true,
                    },
                ])
            );

            setForm({
                nombreContacto: selectedAlerta.NombreContacto || '',
                empresaContacto: selectedAlerta.FKICEmpresa != null ? String(selectedAlerta.FKICEmpresa) : '',
                rolTrafico: selectedAlerta._id_rol_trafico != null
                    ? String(selectedAlerta._id_rol_trafico)
                    : (selectedAlerta.RolTrafico != null ? String(selectedAlerta.RolTrafico) : ''),
                movil: selectedAlerta.Telefono || '',
                areaCode: selectedAlerta.AreaCode || '',
                email: selectedAlerta.Mail || '',
                alertas: alertasFromApi,
                repTrafico: selectedAlerta.ReportTrafico ? 'Activado' : 'Desactivado',
                autoReporte: (selectedAlerta.AutoReporte ?? selectedAlerta.Autoreporte)
                    ? 'Activado'
                    : 'Desactivado',
            });

            setSelectedId(selectedAlerta.ID || null);
            return;
        }

        setSelectedId(null);
        setTipoMail('usuarios');
        setForm(createInitialForm());
    }, [selectedAlerta]);

    return (
        <div>
            <h5 className="mb-4" style={{ backgroundColor: '#006699', padding: '10px', color: 'white' }}>
                Información General
            </h5>

            <MDBRow className="mb-3 g-0">
                <MDBCol size="4">
                    <label className="form-label">TIPO MAILS</label>
                </MDBCol>
                <MDBCol size="8" className="d-flex gap-3">
                    <SwitchButton3
                        checked={tipoMail === 'usuarios'}
                        onChange={(active) => active && setTipoMail('usuarios')}
                        type="Contacto Usuarios"
                        disabled={Boolean(selectedId) || catalogLoading}
                    />
                    <SwitchButton3
                        checked={tipoMail === 'proyectos'}
                        onChange={(active) => active && setTipoMail('proyectos')}
                        type="Contacto Proyectos"
                        disabled={Boolean(selectedId) || catalogLoading}
                    />
                </MDBCol>
            </MDBRow>

            <MDBRow className="mb-3 g-0">
                <MDBCol md="6" className="pe-1">
                    <label className="form-label">NOMBRE CONTACTO</label>
                    <input
                        type="text"
                        name="nombreContacto"
                        className="form-control"
                        value={form.nombreContacto}
                        onChange={handleChange}
                        placeholder="Nombre de contacto"
                        style={{ boxSizing: 'border-box' }}
                    />
                </MDBCol>
                <MDBCol md="6" className="ps-1">
                    <label className="form-label">EMPRESA CONTACTO</label>
                    <select
                        name="empresaContacto"
                        className="form-control"
                        value={form.empresaContacto}
                        onChange={handleChange}
                        disabled={tipoMail !== 'usuarios' || catalogLoading}
                        style={{ boxSizing: 'border-box' }}
                    >
                        <option value="">{catalogLoading ? 'Cargando empresas...' : '-No Asignado-'}</option>
                        {empresas.map((empresa, index) => (
                            <option key={index} value={empresa.IdEmpresa}>{empresa.NombreEmpresa}</option>
                        ))}
                    </select>
                </MDBCol>
            </MDBRow>

            <MDBRow className="mb-3 g-0">
                <MDBCol md="4" className="pe-1">
                    <label className="form-label">ROL TRÁFICO</label>
                    <select
                        name="rolTrafico"
                        className="form-control"
                        value={form.rolTrafico}
                        onChange={handleChange}
                        disabled={tipoMail === 'usuarios' || catalogLoading}
                        style={{ boxSizing: 'border-box' }}
                    >
                        <option value="">{catalogLoading ? 'Cargando roles...' : '-NO-'}</option>
                        {roles.map((rol, index) => (
                            <option key={index} value={rol.id_roltrafico}>{rol.descr_roltrafico}</option>
                        ))}
                    </select>
                </MDBCol>
                <MDBCol md="4" className="px-1">
                    <label className="form-label">MÓVIL</label>
                    <PhoneInput
                        country="co"
                        value={form.areaCode ? `${form.areaCode}${form.movil}` : form.movil}
                        onChange={handlePhoneChange}
                        placeholder="Número de móvil"
                        enableSearch
                        countryCodeEditable={false}
                        inputProps={{
                            name: 'movil',
                        }}
                        containerStyle={{ width: '100%' }}
                        buttonStyle={{
                            borderTopLeftRadius: '0.375rem',
                            borderBottomLeftRadius: '0.375rem',
                            borderColor: '#d9dee3',
                        }}
                        inputStyle={{
                            width: '100%',
                            height: '38px',
                            borderRadius: '0.375rem',
                            borderColor: '#d9dee3',
                            boxSizing: 'border-box',
                            paddingLeft: '48px',
                        }}
                        searchStyle={{ width: '90%' }}
                    />
                </MDBCol>
                <MDBCol md="4" className="ps-1">
                    <label className="form-label">EMAIL</label>
                    <input
                        type="email"
                        name="email"
                        className="form-control"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="Correo electrónico"
                        style={{ boxSizing: 'border-box' }}
                    />
                </MDBCol>
            </MDBRow>

            <h5 className="mb-4 mt-5" style={{ backgroundColor: '#006699', padding: '10px', color: 'white' }}>
                Información Alertas
            </h5>

            <MDBRow className="mb-3 g-0">
                <MDBCol size="12">
                    <h6>Tipos Alertas</h6>
                </MDBCol>
            </MDBRow>

            {ALERT_TYPES.map((type) => (
                <MDBRow key={type.base} className="mb-2 g-0">
                    <MDBCol size="4">{type.label}</MDBCol>

                    <MDBCol size="4">
                        <FormGroup check>
                            <Label check>
                                <Input
                                    type="checkbox"
                                    name={`alertas.${type.base}.T`}
                                    checked={form.alertas[type.base]?.T || false}
                                    onChange={handleChange}
                                />{' '}SMS
                            </Label>
                        </FormGroup>
                    </MDBCol>

                    <MDBCol size="4">
                        <FormGroup check>
                            <Label check>
                                <Input
                                    type="checkbox"
                                    name={`alertas.${type.base}.M`}
                                    checked={form.alertas[type.base]?.M || false}
                                    onChange={handleChange}
                                />{' '}Mail
                            </Label>
                        </FormGroup>
                    </MDBCol>
                </MDBRow>
            ))}

            <MDBRow className="mb-3 g-0">
                <MDBCol md="4" className="d-flex align-items-center">
                    <label style={{ marginBottom: 0 }}>REP. TRÁFICO</label>
                </MDBCol>
                <MDBCol md="4" className="d-flex align-items-center">
                    <SwitchButton3
                        checked={form.repTrafico === 'Activado'}
                        onChange={(active) => setForm((current) => ({
                            ...current,
                            repTrafico: active ? 'Activado' : 'Desactivado',
                        }))}
                        type=""
                        disabled={false}
                    />
                </MDBCol>
                <MDBCol md="4" className="d-flex align-items-center">
                    <div className="d-flex align-items-center gap-2">
                        <SwitchButton3
                            checked={form.autoReporte === 'Activado'}
                            onChange={(active) => setForm((current) => ({
                                ...current,
                                autoReporte: active ? 'Activado' : 'Desactivado',
                            }))}
                            type=""
                            disabled={false}
                        />
                        <label style={{ marginBottom: 0 }}>Autoreporte</label>
                    </div>
                </MDBCol>
            </MDBRow>

            <div className="d-flex justify-content-end">
                <button
                    className="btn-primary-modals-maestros"
                    onClick={handleSubmit}
                    disabled={catalogLoading || submitLoading}
                >
                    {submitLoading ? 'Guardando...' : selectedId ? 'Actualizar' : 'Crear'}
                </button>
            </div>
        </div>
    );
};

export default FormModalCreateAlerta;

